import { Color, CubeTextureLoader, MathUtils, Mesh, MeshBasicMaterial, MultiplyBlending, PlaneGeometry, Vector2, Vector3 } from 'three';
import { Reflector } from 'three/examples/jsm/objects/Reflector';
import { store } from '../Store';
import { BakedSceneMaterial } from './materials/BakedSceneMaterial';
import Scene from './Scene';
import { Vignetting } from './Vignetting';

export class RoomBakedScene extends Scene {
  constructor(options) {
    super({
      name: "room_baked",
      filepath: "assets/scenes/room_baked/room_baked.gltf"
    });

    this.hotspots = [];

    this.reflectorPixelRatio = 0.5;

    this.background = new Color(0x000000);
  }

  onLoad(sceneViewer) {
    this.initMaterials();
    this.initHotspots();
    this.initViewpoints(sceneViewer.cameraController);
    this.initReflector();
    this.initVignetting();
    this.onResize(window.innerWidth, window.innerHeight, sceneViewer.cameraController);
  }

  initMaterials() {

    const cubeLoader = new CubeTextureLoader();
    cubeLoader.setPath("assets/textures/")
    const glassEnvmap = cubeLoader.load(["glass_envmap.jpg", "glass_envmap.jpg", "glass_envmap.jpg", "glass_envmap.jpg", "glass_envmap.jpg", "glass_envmap.jpg"]);

    this.traverse(node => {
      if (node instanceof Mesh && node.material) {

        switch(node.material.name) {
          case "armatures":
            node.material = new MeshBasicMaterial({
              alphaMap: node.material.map,
              color: 0x050505,
              transparent: true
            });
            break;
          case "black_screens":
            node.material = new MeshBasicMaterial({
              color: node.material.color,
              envMap: glassEnvmap
            });
            break;
          case "window_glass":
            node.material = new MeshBasicMaterial({
              color: 0xffffff,
              transparent: true,
              opacity: 0.1,
              envMap: glassEnvmap
            });
            break;
          case "window_border":
            node.material = new MeshBasicMaterial({
              color: node.material.color,
              envMap: glassEnvmap,
              reflectivity: 0.25
            });
            break;            
          default:
            const newMaterial = new BakedSceneMaterial({
              map: node.material.map
            });
    
            node.material = newMaterial;
            break;
        }
      }
    });
  }

  initVignetting() {
    const vignetting = new Vignetting();
  
    this.add(vignetting);
    vignetting.fadeIn();
  }

  initReflector() {
    const reflectorObj = this.getObjectByName("reflector");
    reflectorObj.visible = false;

    const visibleObjects = ["Scene", "Scan_Base_Body_low001", "Driver_Scan_low001", "Lateral_Screens_low001", "group1", "group2"];

    const reflector = new Reflector(new PlaneGeometry(1, 1), {
      textureWidth: window.innerWidth * this.reflectorPixelRatio,
      textureHeight: window.innerHeight * this.reflectorPixelRatio,
      color: 0xffffff
    });

    reflector.position.copy(reflectorObj.position);
    reflector.rotation.x = -Math.PI / 2;
    reflector.scale.set(0.641, 2.1, 1);
    reflector.material.blending = MultiplyBlending;

    this.reflector = reflector;

    const defaultOnBeforeRender = reflector.onBeforeRender;

    reflector.onBeforeRender = (renderer, scene, camera) => {

      scene.traverse(node => {
        node.wasVisible = node.visible;

        if (visibleObjects.indexOf(node.name) > -1) {
          node.visible = true;
        } else {
          node.visible = false;
        }
      });

      scene.visible = true;

      defaultOnBeforeRender(renderer, scene, camera);
    };

    reflector.onAfterRender = () => {
      this.traverse(node => {
        node.visible = node.wasVisible;
      });
    };

    this.add(reflector);
  }

  initHotspots() {
    const parent = this.getObjectByName("hotspots");

    parent.children.forEach(hotspot => {
      this.hotspots.push(hotspot);
    });
  }

  initViewpoints(cameraController) {
    cameraController.setViewpoints(this.getObjectByName("viewpoints").children);
  }

  onResize(width, height, cameraController) {
    const isPortraitMode = width < height;

    this.setCameraTargetAndBounds(isPortraitMode, cameraController);

    if (this.reflector) {
      this.reflector.getRenderTarget().setSize(
        width * this.reflectorPixelRatio,
        height * this.reflectorPixelRatio
      );
    }
  }

  setCameraTargetAndBounds(isPortraitMode, cameraController) {
    const cameraDistance = isPortraitMode ? 3 : 7;

    cameraController.setTarget(0, 1.5, isPortraitMode ? 4 : 0, cameraDistance);
    cameraController.setDistance(cameraDistance);
    cameraController.setAzimuthalAngles(isPortraitMode ? -1.1 : -0.5, isPortraitMode ? 1.1 : 0.5);
  }

  update = (() => {
    const tmpPos = new Vector3();
    const tmpCoords = new Vector2();

    return (cameraController) => {
      const camera = cameraController.camera;
  
      camera.updateMatrixWorld();
  
      this.hotspots.forEach((hotspot, index) => {
        const size = MathUtils.mapLinear(hotspot.position.distanceTo(camera.position), 4, 8, 1, 0.5);

        tmpPos.copy(hotspot.position).project(camera);
  
        tmpCoords.set(
          MathUtils.mapLinear(tmpPos.x, -1, 1, 0, window.innerWidth),
          MathUtils.mapLinear(tmpPos.y, -1, 1, window.innerHeight, 0)
        );
  
        store.setHotspot(index, hotspot.name, tmpCoords.x, tmpCoords.y, size);
      }); 
    }
  })();
}